/**
 * AccountNode - Financial account node component
 *
 * Renders an account node with fixed height, displaying the account name
 * and balance prominently.
 *
 * WP-FLOW-6: Updated to use light background + border styling
 * - Liability accounts (credit, loan, other_liability) use amber colors
 * - Asset accounts use tier-based colors (blue/purple/gray)
 * - Added optional liability indicator icon
 */

import { memo, useState, useCallback } from 'react';
import type { AccountNode as AccountNodeType, NodeProps } from '../types';
import { isLiabilityAccount } from '../types';
import { NODE, COLORS, A11Y } from '../constants';
import { formatCurrency } from '@/utils/format';
import { truncate, ACCOUNT_NAME_MAX_CHARS } from '../utils';

export interface AccountNodeProps extends NodeProps<AccountNodeType> {
  /** Called when mouse enters the node */
  onMouseEnter?: (node: AccountNodeType, event: React.MouseEvent) => void;
  /** Called when mouse leaves the node */
  onMouseLeave?: (node: AccountNodeType) => void;
  /** Whether this node is currently highlighted */
  isHighlighted?: boolean;
}

/**
 * Get the color configuration for an account node
 * Liabilities (Tier D) use brown colors, assets use tier-based colors
 */
function getNodeColors(
  accountType: string,
  tier: 'A' | 'B' | 'C' | 'D'
): { background: string; border: string; text: string } {
  // Tier D or liability account types use brown/liability colors
  if (tier === 'D' || isLiabilityAccount(accountType)) {
    return COLORS.account.tierD;
  }

  // Asset accounts use tier-based colors
  const tierKey = `tier${tier}` as 'tierA' | 'tierB' | 'tierC';
  return COLORS.account[tierKey];
}

function AccountNodeComponent({
  node,
  x,
  y,
  width,
  height,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHighlighted = false,
}: AccountNodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleClick = useCallback(() => {
    onClick?.(node);
  }, [onClick, node]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(node);
      }
    },
    [onClick, node]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      setIsHovered(true);
      onMouseEnter?.(node, e);
    },
    [node, onMouseEnter]
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onMouseLeave?.(node);
  }, [node, onMouseLeave]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Get colors based on account type (liability vs asset)
  const isLiability = isLiabilityAccount(node.accountType);
  const nodeColors = getNodeColors(node.accountType, node.tier);

  // Determine if balance is negative (credit cards, loans)
  const isNegativeBalance = node.balance < 0;

  // Format balance - show absolute value with appropriate styling
  const displayBalance = formatCurrency(Math.abs(node.balance));
  const balancePrefix = isNegativeBalance ? '-' : '';

  // Balance text color: red for negative, otherwise use node text color
  const balanceTextColor = isNegativeBalance ? COLORS.text.negative : nodeColors.text;

  // Calculate text positions (centered in node)
  const centerX = x + width / 2;
  const nameY = y + height * 0.35;
  const balanceY = y + height * 0.7;

  // Visual state
  const showHighlight = isHovered || isFocused || isHighlighted;

  // Aria label for accessibility (includes account type for context)
  const accountTypeLabel = isLiability ? 'liability' : 'asset';
  const ariaLabel = `${node.name} (${accountTypeLabel}), Balance: ${balancePrefix}${displayBalance}`;

  return (
    <g
      className="account-node"
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Focus ring (rendered behind node) */}
      {isFocused && (
        <rect
          x={x - 3}
          y={y - 3}
          width={width + 6}
          height={height + 6}
          rx={NODE.BORDER_RADIUS + 2}
          ry={NODE.BORDER_RADIUS + 2}
          fill="none"
          stroke={A11Y.FOCUS_RING.split(' ')[2]}
          strokeWidth={2}
        />
      )}

      {/* Node rectangle with light background and border */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={NODE.BORDER_RADIUS}
        ry={NODE.BORDER_RADIUS}
        fill={nodeColors.background}
        stroke={nodeColors.border}
        strokeWidth={NODE.BORDER_WIDTH}
        opacity={showHighlight ? 1 : 0.95}
        filter={showHighlight ? 'url(#node-shadow-hover)' : 'url(#node-shadow)'}
        className="transition-all duration-150 ease-in-out"
      />

      {/* Hover highlight effect */}
      {showHighlight && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={NODE.BORDER_RADIUS}
          ry={NODE.BORDER_RADIUS}
          fill={nodeColors.border}
          opacity={0.08}
          className="pointer-events-none"
        />
      )}

      {/* Liability indicator icon (small diamond) */}
      {isLiability && (
        <text
          x={x + 10}
          y={y + 14}
          fontSize={9}
          fill={nodeColors.border}
          aria-hidden="true"
        >
          {'\u25C6'}
        </text>
      )}

      {/* Account name - WP-FLOW-7: truncated to fit 160px node width */}
      <text
        x={centerX}
        y={nameY}
        textAnchor="middle"
        fill={nodeColors.text}
        fontSize={12}
        fontWeight={showHighlight ? 600 : 500}
        className="sankey-label"
      >
        {truncate(node.name, ACCOUNT_NAME_MAX_CHARS)}
      </text>

      {/* Balance - displayed prominently */}
      <text
        x={centerX}
        y={balanceY}
        textAnchor="middle"
        fill={balanceTextColor}
        fontSize={14}
        fontWeight={600}
        className="sankey-balance"
      >
        {balancePrefix}{displayBalance}
      </text>
    </g>
  );
}

export const AccountNode = memo(AccountNodeComponent);
export default AccountNode;
